/**
 * Modal d'upload en lot de scans (PDF) avec routage automatique via QR code.
 *
 * - Sélection multi-fichiers depuis un dossier
 * - Le backend lit le QR de chaque PDF et route vers le bon relevé
 * - N'enclenche PAS l'OCR : l'utilisateur clique "Traiter" relevé par relevé
 * - Option "Remplacer" pour écraser un scan existant
 * - Rapport détaillé par fichier (success / remplace / déjà uploadé / déjà traité / non reconnu / erreur)
 *
 * Mutualisé pour les relevés Devoirs et Correction (examens).
 */
import React, { useState, useRef, useCallback } from 'react';
import {
  X, Upload, FileText, Loader, CheckCircle2, XCircle, AlertTriangle,
  FolderOpen, RefreshCw, Download, Clock, FileCheck, FileX, Trash2,
} from 'lucide-react';
import {
  useUploadBatchQRDevoirsMutation,
  useUploadBatchQRCorrectionMutation,
  useUploadScanDevoirsMutation,
  useUploadScanCorrectionMutation,
  useGetRelevesDevoirsQuery,
  useGetRelevesCorrectionQuery,
  UploadBatchQRResponse,
  FichierBatchQRResult,
  StatutFichierBatchQR,
  ReleveDevoirs,
  ReleveCorrection,
} from '../../services/ocrApi';

interface Props {
  open: boolean;
  onClose: () => void;
  type: 'devoirs' | 'correction';
}

const STATUT_META: Record<StatutFichierBatchQR, {
  label: string;
  color: string;
  bg: string;
  icon: React.ReactNode;
}> = {
  success: {
    label: 'Uploadé',
    color: 'text-green-700 dark:text-green-300',
    bg: 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800',
    icon: <CheckCircle2 size={14} className="text-green-600" />,
  },
  remplace: {
    label: 'Remplacé',
    color: 'text-blue-700 dark:text-blue-300',
    bg: 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800',
    icon: <RefreshCw size={14} className="text-blue-600" />,
  },
  deja_uploade: {
    label: 'Déjà uploadé',
    color: 'text-amber-700 dark:text-amber-300',
    bg: 'bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800',
    icon: <FileCheck size={14} className="text-amber-600" />,
  },
  deja_traite: {
    label: 'Déjà traité',
    color: 'text-purple-700 dark:text-purple-300',
    bg: 'bg-purple-50 dark:bg-purple-900/20 border-purple-200 dark:border-purple-800',
    icon: <FileX size={14} className="text-purple-600" />,
  },
  non_reconnu: {
    label: 'Non reconnu',
    color: 'text-orange-700 dark:text-orange-300',
    bg: 'bg-orange-50 dark:bg-orange-900/20 border-orange-200 dark:border-orange-800',
    icon: <AlertTriangle size={14} className="text-orange-600" />,
  },
  erreur: {
    label: 'Erreur',
    color: 'text-red-700 dark:text-red-300',
    bg: 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800',
    icon: <XCircle size={14} className="text-red-600" />,
  },
};

/** État d'une association manuelle pour un fichier "non reconnu". */
interface ManualResolution {
  releveId: number | null;
  status: 'pending' | 'uploading' | 'success' | 'error';
  message?: string;
}

const UploadBatchParQRModal: React.FC<Props> = ({ open, onClose, type }) => {
  const [files, setFiles] = useState<File[]>([]);
  const [remplacer, setRemplacer] = useState(false);
  const [report, setReport] = useState<UploadBatchQRResponse | null>(null);
  const [filtreStatut, setFiltreStatut] = useState<StatutFichierBatchQR | 'tous'>('tous');
  // Associations manuelles : nom de fichier -> état
  const [manualResolutions, setManualResolutions] = useState<Record<string, ManualResolution>>({});
  const inputRef = useRef<HTMLInputElement>(null);

  const [uploadDevoirs, { isLoading: loadingDevoirs }] = useUploadBatchQRDevoirsMutation();
  const [uploadCorrection, { isLoading: loadingCorrection }] = useUploadBatchQRCorrectionMutation();
  const [uploadSingleDevoirs] = useUploadScanDevoirsMutation();
  const [uploadSingleCorrection] = useUploadScanCorrectionMutation();
  const isLoading = loadingDevoirs || loadingCorrection;

  // Charger la liste des relevés uniquement quand le modal est ouvert ET qu'il y a
  // au moins un fichier non_reconnu dans le rapport (sinon pas la peine).
  const hasNonReconnu = !!report && report.results.some(r => r.statut === 'non_reconnu');
  const { data: relevesDevoirs } = useGetRelevesDevoirsQuery(undefined, {
    skip: !open || type !== 'devoirs' || !hasNonReconnu,
  });
  const { data: relevesCorrection } = useGetRelevesCorrectionQuery(undefined, {
    skip: !open || type !== 'correction' || !hasNonReconnu,
  });
  const relevesDispo: (ReleveDevoirs | ReleveCorrection)[] = type === 'devoirs'
    ? (relevesDevoirs || [])
    : (relevesCorrection || []);

  const titre = type === 'devoirs' ? 'Upload en lot — Relevés de Devoirs' : 'Upload en lot — Relevés d\'Examens';
  // Classes Tailwind statiques (Tailwind JIT n'expand pas les template literals)
  const accent = type === 'devoirs'
    ? {
        text: 'text-blue-600',
        btn: 'bg-blue-600 hover:bg-blue-700',
        infoBg: 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800',
        infoText: 'text-blue-800 dark:text-blue-300',
      }
    : {
        text: 'text-indigo-600',
        btn: 'bg-indigo-600 hover:bg-indigo-700',
        infoBg: 'bg-indigo-50 dark:bg-indigo-900/20 border-indigo-200 dark:border-indigo-800',
        infoText: 'text-indigo-800 dark:text-indigo-300',
      };

  const handleFileSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files) return;
    const list = Array.from(e.target.files).filter(f =>
      /\.(pdf|jpg|jpeg|png|tiff?)$/i.test(f.name)
    );
    setFiles(prev => {
      const seen = new Set(prev.map(f => f.name + ':' + f.size));
      const nouveaux = list.filter(f => !seen.has(f.name + ':' + f.size));
      return [...prev, ...nouveaux];
    });
    setReport(null);
    // Reset l'input pour permettre une re-sélection des mêmes fichiers
    if (inputRef.current) inputRef.current.value = '';
  }, []);

  const removeFile = (idx: number) => {
    setFiles(prev => prev.filter((_, i) => i !== idx));
  };

  const clearAll = () => {
    setFiles([]);
    setReport(null);
    setFiltreStatut('tous');
    setManualResolutions({});
  };

  /** Lance l'upload single d'un fichier non_reconnu vers un relevé manuellement choisi. */
  const handleManualAssoc = async (fileName: string, releveId: number, replaceExisting: boolean) => {
    // Retrouver le File original gardé en mémoire
    const file = files.find(f => f.name === fileName);
    if (!file || !releveId) return;
    setManualResolutions(prev => ({
      ...prev,
      [fileName]: { releveId, status: 'uploading' },
    }));
    try {
      const formData = new FormData();
      formData.append('fichier', file);
      // L'endpoint single accepte ?force=true pour bypasser la validation QR
      // (utile justement quand le QR est illisible — c'est notre cas ici).
      // Pour devoirs, le param URL est différent : on passe via FormData ne marche pas,
      // on enrichit l'URL manuellement.
      if (type === 'devoirs') {
        await uploadSingleDevoirs({ releveId, formData }).unwrap();
      } else {
        await uploadSingleCorrection({ releveId, formData }).unwrap();
      }
      setManualResolutions(prev => ({
        ...prev,
        [fileName]: { releveId, status: 'success', message: 'Associé manuellement' },
      }));
    } catch (e: any) {
      setManualResolutions(prev => ({
        ...prev,
        [fileName]: {
          releveId,
          status: 'error',
          message: e?.data?.error || e?.data?.detail || e?.message || 'Erreur inconnue',
        },
      }));
    }
  };

  const handleUpload = async () => {
    if (files.length === 0) return;
    const formData = new FormData();
    files.forEach(f => formData.append('files', f));
    formData.append('remplacer', remplacer ? 'true' : 'false');
    try {
      const res = type === 'devoirs'
        ? await uploadDevoirs(formData).unwrap()
        : await uploadCorrection(formData).unwrap();
      setReport(res);
    } catch (e: any) {
      alert(`Erreur lors de l'upload : ${e?.data?.error || e?.message || 'inconnue'}`);
    }
  };

  const exportRapportCSV = () => {
    if (!report) return;
    const cols = [
      'fichier', 'statut', 'message', 'code_releve', 'matiere_code', 'matiere_nom',
      'niveau', 'option', 'semestre', 'enseignant_nom',
      ...(type === 'correction' ? ['type_examen', 'classes'] : []),
      'statut_releve_avant', 'statut_releve_apres',
    ];
    const rows = [
      cols.join(';'),
      ...report.results.map(r =>
        cols.map(c => {
          const v = (r as any)[c];
          if (v === null || v === undefined) return '';
          return String(v).replace(/[;\n\r]/g, ' ');
        }).join(';')
      ),
    ];
    const csv = '﻿' + rows.join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    const ts = new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-');
    a.href = url;
    a.download = `rapport_upload_${type}_${ts}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const filteredResults = report
    ? (filtreStatut === 'tous'
        ? report.results
        : report.results.filter(r => r.statut === filtreStatut))
    : [];

  if (!open) return null;

  const totalSize = files.reduce((s, f) => s + f.size, 0);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="px-5 py-3 border-b dark:border-gray-700 flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
            <Upload size={20} className={accent.text} />
            {titre}
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300"
            disabled={isLoading}
          >
            <X size={22} />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          {/* Étape 1 : explication + sélection */}
          {!report && (
            <>
              <div className={`p-3 ${accent.infoBg} border rounded text-xs ${accent.infoText} space-y-1`}>
                <div><strong>Comment ça marche :</strong> sélectionnez plusieurs PDFs scannés. Chaque fichier est routé automatiquement vers le bon relevé via son QR code en haut de page.</div>
                <div><strong>L'OCR n'est PAS lancé automatiquement.</strong> Une fois les fichiers rangés, vous pourrez cliquer "Traiter" relevé par relevé après vérification.</div>
              </div>

              {/* Drop zone / picker */}
              <div className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-6 text-center">
                <input
                  ref={inputRef}
                  type="file"
                  multiple
                  accept=".pdf,.jpg,.jpeg,.png,.tiff,.tif"
                  onChange={handleFileSelect}
                  className="hidden"
                  id="batch-file-input"
                />
                <label
                  htmlFor="batch-file-input"
                  className={`inline-flex items-center gap-2 px-4 py-2 ${accent.btn} text-white rounded-md cursor-pointer text-sm font-medium`}
                >
                  <FolderOpen size={16} />
                  Sélectionner les fichiers PDF
                </label>
                <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                  PDF / JPG / PNG / TIFF — vous pouvez sélectionner un dossier entier
                </p>
              </div>

              {/* Liste fichiers sélectionnés */}
              {files.length > 0 && (
                <div className="border border-gray-200 dark:border-gray-700 rounded">
                  <div className="px-3 py-2 bg-gray-50 dark:bg-gray-700/50 border-b dark:border-gray-700 flex items-center justify-between">
                    <div className="text-sm font-medium text-gray-700 dark:text-gray-200">
                      {files.length} fichier{files.length > 1 ? 's' : ''} sélectionné{files.length > 1 ? 's' : ''}
                      <span className="text-xs text-gray-500 ml-2">
                        ({(totalSize / (1024 * 1024)).toFixed(1)} Mo)
                      </span>
                    </div>
                    <button
                      onClick={clearAll}
                      className="text-xs text-red-600 dark:text-red-400 hover:underline flex items-center gap-1"
                    >
                      <Trash2 size={12} /> Tout effacer
                    </button>
                  </div>
                  <div className="max-h-60 overflow-y-auto">
                    {files.map((f, idx) => (
                      <div
                        key={`${f.name}-${idx}`}
                        className="px-3 py-1.5 text-xs flex items-center justify-between border-b dark:border-gray-700 last:border-b-0"
                      >
                        <div className="flex items-center gap-2 min-w-0">
                          <FileText size={12} className="text-gray-400 shrink-0" />
                          <span className="truncate text-gray-700 dark:text-gray-300">{f.name}</span>
                          <span className="text-gray-400 shrink-0">
                            {(f.size / 1024).toFixed(0)} Ko
                          </span>
                        </div>
                        <button
                          onClick={() => removeFile(idx)}
                          className="text-gray-400 hover:text-red-600 ml-2 shrink-0"
                          title="Retirer"
                        >
                          <X size={14} />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Option remplacer */}
              <label className="flex items-start gap-2 cursor-pointer p-2 border border-gray-200 dark:border-gray-700 rounded hover:bg-gray-50 dark:hover:bg-gray-700/30">
                <input
                  type="checkbox"
                  checked={remplacer}
                  onChange={(e) => setRemplacer(e.target.checked)}
                  className="mt-0.5 rounded"
                />
                <div className="text-sm">
                  <div className="font-medium text-gray-800 dark:text-gray-200">
                    Remplacer les scans existants
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">
                    Si décoché, les relevés ayant déjà un scan seront ignorés (rapport "déjà uploadé").
                    Les relevés déjà <strong>traités/vérifiés/validés</strong> ne sont jamais touchés.
                  </div>
                </div>
              </label>
            </>
          )}

          {/* Étape 2 : rapport */}
          {report && (
            <>
              {/* Résumé global */}
              <div className="grid grid-cols-2 md:grid-cols-7 gap-2">
                {([
                  { key: 'total', label: 'Total', val: report.summary.total,
                    activeBg: 'bg-gray-100 dark:bg-gray-700/60 border-gray-300 dark:border-gray-600',
                    text: 'text-gray-700 dark:text-gray-200' },
                  { key: 'success', label: 'Uploadé', val: report.summary.success,
                    activeBg: 'bg-green-100 dark:bg-green-900/30 border-green-300 dark:border-green-700',
                    text: 'text-green-700 dark:text-green-300' },
                  { key: 'remplace', label: 'Remplacé', val: report.summary.remplace,
                    activeBg: 'bg-blue-100 dark:bg-blue-900/30 border-blue-300 dark:border-blue-700',
                    text: 'text-blue-700 dark:text-blue-300' },
                  { key: 'deja_uploade', label: 'Déjà uploadé', val: report.summary.deja_uploade,
                    activeBg: 'bg-amber-100 dark:bg-amber-900/30 border-amber-300 dark:border-amber-700',
                    text: 'text-amber-700 dark:text-amber-300' },
                  { key: 'deja_traite', label: 'Déjà traité', val: report.summary.deja_traite,
                    activeBg: 'bg-purple-100 dark:bg-purple-900/30 border-purple-300 dark:border-purple-700',
                    text: 'text-purple-700 dark:text-purple-300' },
                  { key: 'non_reconnu', label: 'Non reconnu', val: report.summary.non_reconnu,
                    activeBg: 'bg-orange-100 dark:bg-orange-900/30 border-orange-300 dark:border-orange-700',
                    text: 'text-orange-700 dark:text-orange-300' },
                  { key: 'erreur', label: 'Erreur', val: report.summary.erreur,
                    activeBg: 'bg-red-100 dark:bg-red-900/30 border-red-300 dark:border-red-700',
                    text: 'text-red-700 dark:text-red-300' },
                ] as const).map(s => {
                  const active = (filtreStatut === s.key) || (s.key === 'total' && filtreStatut === 'tous');
                  return (
                    <button
                      key={s.key}
                      onClick={() => setFiltreStatut(s.key === 'total' ? 'tous' : s.key as StatutFichierBatchQR)}
                      className={`p-2 rounded border text-center hover:shadow ${
                        active
                          ? s.activeBg
                          : 'bg-white dark:bg-gray-700 border-gray-200 dark:border-gray-600'
                      }`}
                    >
                      <div className={`text-xl font-bold ${s.text}`}>{s.val}</div>
                      <div className="text-[10px] text-gray-600 dark:text-gray-400 uppercase tracking-wide">
                        {s.label}
                      </div>
                    </button>
                  );
                })}
              </div>

              {/* Métadonnées du rapport */}
              <div className="text-xs text-gray-600 dark:text-gray-400 flex flex-wrap items-center gap-3 bg-gray-50 dark:bg-gray-900/30 rounded p-2">
                <span className="flex items-center gap-1">
                  <Clock size={12} />
                  {new Date(report.date_upload).toLocaleString('fr-FR')}
                </span>
                {report.uploaded_by && (
                  <span>Par : <strong>{report.uploaded_by}</strong></span>
                )}
                {report.remplacer_actif && (
                  <span className="px-1.5 py-0.5 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded">
                    Mode "Remplacer" activé
                  </span>
                )}
                <button
                  onClick={exportRapportCSV}
                  className="ml-auto flex items-center gap-1 px-2 py-1 text-xs bg-emerald-600 hover:bg-emerald-700 text-white rounded"
                >
                  <Download size={12} /> Exporter CSV
                </button>
              </div>

              {/* Liste détaillée */}
              <div className="space-y-1.5">
                {filteredResults.length === 0 ? (
                  <div className="text-center text-sm text-gray-500 py-6">
                    Aucun fichier dans cette catégorie.
                  </div>
                ) : filteredResults.map((r, idx) => {
                  const resolution = manualResolutions[r.fichier];
                  // Si resolution.success → afficher comme success
                  const effectiveStatut: StatutFichierBatchQR =
                    resolution?.status === 'success' ? 'success' : r.statut;
                  const meta = STATUT_META[effectiveStatut];
                  const isNonReconnu = r.statut === 'non_reconnu' && resolution?.status !== 'success';
                  return (
                    <div
                      key={idx}
                      className={`border rounded p-2.5 text-xs ${meta.bg}`}
                    >
                      <div className="flex items-start gap-2 flex-wrap">
                        {meta.icon}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-baseline justify-between gap-2 flex-wrap">
                            <span className="font-mono font-semibold text-gray-900 dark:text-gray-100 truncate">
                              {r.fichier}
                            </span>
                            <span className={`text-[10px] px-1.5 py-0.5 rounded font-semibold ${meta.color} bg-white/60 dark:bg-black/20`}>
                              {meta.label}
                            </span>
                          </div>
                          <div className={`mt-0.5 text-[11px] ${meta.color}`}>
                            {resolution?.status === 'success' ? resolution.message : r.message}
                          </div>
                          {r.code_releve && (
                            <div className="mt-1 grid grid-cols-2 md:grid-cols-3 gap-x-3 gap-y-0.5 text-[11px] text-gray-700 dark:text-gray-300">
                              <span><strong>Code :</strong> <span className="font-mono">{r.code_releve}</span></span>
                              <span><strong>Matière :</strong> {r.matiere_code} — {r.matiere_nom}</span>
                              <span><strong>Niveau :</strong> {r.niveau} {r.semestre}</span>
                              {type === 'correction' && r.classes && (
                                <span><strong>Classes :</strong> {r.classes}</span>
                              )}
                              {type === 'correction' && r.type_examen && (
                                <span><strong>Type :</strong> {r.type_examen}</span>
                              )}
                              <span className="md:col-span-1"><strong>Option :</strong> {r.option}</span>
                              {r.enseignant_nom && (
                                <span className="md:col-span-3"><strong>Formateur :</strong> {r.enseignant_nom}</span>
                              )}
                              {r.statut_releve_avant && r.statut_releve_apres && r.statut_releve_avant !== r.statut_releve_apres && (
                                <span className="md:col-span-3 text-blue-600 dark:text-blue-400">
                                  Statut : {r.statut_releve_avant} → <strong>{r.statut_releve_apres}</strong>
                                </span>
                              )}
                            </div>
                          )}
                          {!r.code_releve && r.code_detecte && (
                            <div className="mt-0.5 text-[11px] text-gray-600 dark:text-gray-400">
                              QR détecté : <span className="font-mono">{r.code_detecte}</span>
                            </div>
                          )}

                          {/* Bloc d'association manuelle pour les fichiers non reconnus */}
                          {isNonReconnu && (
                            <ManualAssocPicker
                              fileName={r.fichier}
                              type={type}
                              releves={relevesDispo}
                              resolution={resolution}
                              accentBtn={accent.btn}
                              onAssoc={handleManualAssoc}
                            />
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="px-5 py-3 border-t dark:border-gray-700 flex justify-between items-center gap-2 flex-wrap">
          {!report ? (
            <>
              <span className="text-xs text-gray-500 dark:text-gray-400">
                {files.length === 0 ? 'Aucun fichier sélectionné' : `${files.length} fichier(s) prêt(s)`}
              </span>
              <div className="flex gap-2">
                <button
                  onClick={onClose}
                  disabled={isLoading}
                  className="px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-200 rounded text-sm hover:bg-gray-300"
                >
                  Annuler
                </button>
                <button
                  onClick={handleUpload}
                  disabled={files.length === 0 || isLoading}
                  className={`flex items-center gap-2 px-4 py-2 ${accent.btn} text-white rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed`}
                >
                  {isLoading ? <Loader size={14} className="animate-spin" /> : <Upload size={14} />}
                  {isLoading ? 'Upload en cours…' : `Uploader ${files.length} fichier(s)`}
                </button>
              </div>
            </>
          ) : (
            <>
              <button
                onClick={() => { setReport(null); setFiles([]); }}
                className="px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-200 rounded text-sm hover:bg-gray-300"
              >
                Nouvel upload
              </button>
              <button
                onClick={onClose}
                className={`px-4 py-2 ${accent.btn} text-white rounded text-sm`}
              >
                Fermer
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

/**
 * Picker manuel pour associer un fichier non_reconnu à un relevé existant.
 * Filtre par texte (code, matière, classe, niveau, formateur), exclut les
 * relevés déjà traités/vérifiés/validés.
 */
interface ManualAssocPickerProps {
  fileName: string;
  type: 'devoirs' | 'correction';
  releves: (ReleveDevoirs | ReleveCorrection)[];
  resolution?: ManualResolution;
  accentBtn: string;
  onAssoc: (fileName: string, releveId: number, replaceExisting: boolean) => void;
}

const ManualAssocPicker: React.FC<ManualAssocPickerProps> = ({
  fileName, type, releves, resolution, accentBtn, onAssoc,
}) => {
  const [search, setSearch] = useState('');
  const [niveau, setNiveau] = useState('');
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [showAll, setShowAll] = useState(false);

  // Filtrer : exclure traite/verifie/valide par défaut
  const filtered = React.useMemo(() => {
    let list = releves;
    if (!showAll) {
      list = list.filter(r => !['traite', 'verifie', 'valide'].includes(r.status));
    }
    if (niveau) {
      list = list.filter(r => r.niveau === niveau);
    }
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      list = list.filter(r =>
        (r.code_releve || '').toLowerCase().includes(q) ||
        (r.matiere_nom || '').toLowerCase().includes(q) ||
        (r.matiere_code || '').toLowerCase().includes(q) ||
        (r.option || '').toLowerCase().includes(q) ||
        ((r as any).classes || '').toLowerCase().includes(q) ||
        (r.enseignant_nom || '').toLowerCase().includes(q)
      );
    }
    return list.slice(0, 50);  // cap pour rester réactif
  }, [releves, search, niveau, showAll]);

  const selected = filtered.find(r => r.id === selectedId)
    ?? releves.find(r => r.id === selectedId);
  const hasExistingScan = !!selected?.fichier_scan;
  const [replaceExisting, setReplaceExisting] = useState(false);

  if (resolution?.status === 'success') {
    return (
      <div className="mt-2 p-2 rounded bg-green-100 dark:bg-green-900/30 text-[11px] text-green-700 dark:text-green-300 flex items-center gap-1.5">
        <CheckCircle2 size={12} /> {resolution.message}
      </div>
    );
  }
  if (resolution?.status === 'error') {
    return (
      <div className="mt-2 space-y-1.5">
        <div className="p-2 rounded bg-red-100 dark:bg-red-900/30 text-[11px] text-red-700 dark:text-red-300 flex items-center gap-1.5">
          <XCircle size={12} /> {resolution.message}
        </div>
        <button
          onClick={() => onAssoc(fileName, resolution.releveId!, replaceExisting)}
          className="px-2 py-1 text-[11px] bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-200 rounded hover:bg-gray-300"
        >
          Réessayer
        </button>
      </div>
    );
  }

  const isUploading = resolution?.status === 'uploading';

  return (
    <div className="mt-2 p-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 space-y-1.5">
      <div className="text-[11px] font-semibold text-gray-700 dark:text-gray-300">
        Associer manuellement à un relevé :
      </div>
      <div className="flex gap-1.5 flex-wrap">
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Rechercher (code, matière, classe, formateur)…"
          className="flex-1 min-w-[160px] px-2 py-1 text-[11px] border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
        />
        <select
          value={niveau}
          onChange={(e) => setNiveau(e.target.value)}
          className="px-2 py-1 text-[11px] border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
        >
          <option value="">Tous niveaux</option>
          <option value="L1">L1</option>
          <option value="L2">L2</option>
          <option value="L3">L3</option>
          <option value="M1">M1</option>
          <option value="M2">M2</option>
        </select>
        <label className="flex items-center gap-1 text-[10px] text-gray-600 dark:text-gray-400 cursor-pointer">
          <input
            type="checkbox"
            checked={showAll}
            onChange={(e) => setShowAll(e.target.checked)}
            className="rounded"
          />
          Inclure traités/validés
        </label>
      </div>

      <div className="max-h-40 overflow-y-auto border border-gray-200 dark:border-gray-700 rounded">
        {filtered.length === 0 ? (
          <div className="text-center text-[10px] text-gray-500 py-2">
            {search || niveau ? 'Aucun relevé correspondant' : 'Tape pour rechercher…'}
          </div>
        ) : filtered.map((r) => {
          const isSel = r.id === selectedId;
          const r2 = r as ReleveCorrection; // pour accéder à .classes / .type_examen
          return (
            <button
              key={r.id}
              onClick={() => setSelectedId(r.id)}
              className={`w-full text-left px-2 py-1 text-[11px] border-b last:border-b-0 border-gray-100 dark:border-gray-700 ${
                isSel
                  ? 'bg-blue-50 dark:bg-blue-900/30'
                  : 'hover:bg-gray-50 dark:hover:bg-gray-700/40'
              }`}
            >
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-mono font-semibold">{r.code_releve}</span>
                <span className="text-gray-700 dark:text-gray-300">
                  {r.matiere_code} — {r.matiere_nom}
                </span>
                <span className="text-gray-500 dark:text-gray-400">
                  {r.niveau} {r.semestre}
                </span>
                {type === 'correction' && r2.classes && (
                  <span className="text-gray-500 dark:text-gray-400">{r2.classes}</span>
                )}
                {r.enseignant_nom && (
                  <span className="text-gray-500 dark:text-gray-400">· {r.enseignant_nom}</span>
                )}
                <span className={`ml-auto text-[9px] px-1 py-0.5 rounded ${
                  r.status === 'valide' ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300' :
                  r.status === 'verifie' ? 'bg-cyan-100 text-cyan-700 dark:bg-cyan-900/30 dark:text-cyan-300' :
                  r.status === 'traite' ? 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-300' :
                  r.fichier_scan ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300' :
                  'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300'
                }`}>
                  {r.fichier_scan ? 'a déjà un scan' : r.status}
                </span>
              </div>
            </button>
          );
        })}
      </div>

      {selected && (
        <div className="text-[10px] text-gray-600 dark:text-gray-400 px-1">
          Sélectionné : <strong>{selected.code_releve}</strong>
          {hasExistingScan && (
            <label className="ml-2 inline-flex items-center gap-1 cursor-pointer">
              <input
                type="checkbox"
                checked={replaceExisting}
                onChange={(e) => setReplaceExisting(e.target.checked)}
                className="rounded"
              />
              <span className="text-amber-700 dark:text-amber-300 font-semibold">
                Remplacer le scan existant
              </span>
            </label>
          )}
        </div>
      )}

      <button
        onClick={() => selectedId && onAssoc(fileName, selectedId, replaceExisting)}
        disabled={!selectedId || isUploading || (hasExistingScan && !replaceExisting)}
        className={`flex items-center gap-1 px-3 py-1 text-[11px] ${accentBtn} text-white rounded disabled:opacity-50 disabled:cursor-not-allowed`}
      >
        {isUploading ? <Loader size={11} className="animate-spin" /> : <Upload size={11} />}
        {isUploading ? 'Association…' : 'Associer & uploader'}
      </button>
    </div>
  );
};

export default UploadBatchParQRModal;
